// react
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// store
import { useRegisterMemberByDomainMutation } from 'store/api/iam';

// models
import { FieldType } from '@karehero/models';

// components
import {
  OnboardingActionType,
  OnboardingEditor,
  OnboardingFlow,
  OnboardingKeni,
} from '@karehero/llama';

// config
import { onboardingKeniConfirmEmailConfig } from 'config/onboarding';

// validation
const validateEmail = (value?: string) => /\S+@\S+\.\S+/.test(value || '');

/**
 * VerifyMember takes the user through the registration flow.
 */
const VerifyMember = () => {
  // state
  const [email, setEmail] = useState<{ value: string }>();

  // hooks
  const [registerMemberByDomain] = useRegisterMemberByDomainMutation();
  const navigate = useNavigate();

  // methods
  const handleRegisterMemberByDomain =
    useCallback(async (): Promise<boolean> => {
      const res = await registerMemberByDomain({
        email: email?.value || '',
      });
      if ('error' in res && 'status' in res.error) {
        return false;
      }

      navigate('/register/invite-code');
      return true;
    }, [registerMemberByDomain, email, navigate]);

  // memos
  const onboardingMemberRegisterConfig = useMemo(
    () => [
      {
        id: 'register',
        Component: OnboardingEditor,
        onBeforeNext: handleRegisterMemberByDomain,
        config: [
          {
            id: 'verify-account',
            title: "You're almost there!",
            subTitle:
              'We lost connection for a moment. Please re-enter your work email to verify.',
            value: email,
            onChange: setEmail,
            fieldDef: {
              label: 'Work email address',
              field: {
                type: FieldType.Email,
              },
              validation: validateEmail,
            },
            isSupport: true,
            actions: [
              {
                label: 'Next',
                onClick: OnboardingActionType.Next,
              },
            ],
          },
        ],
      },
      {
        id: 'keni',
        Component: OnboardingKeni,
        config: onboardingKeniConfirmEmailConfig,
      },
    ],
    [email, handleRegisterMemberByDomain],
  );

  return (
    <OnboardingFlow
      config={onboardingMemberRegisterConfig}
      onComplete={() => {}}
    />
  );
};

VerifyMember.displayName = 'VerifyMember';
export default VerifyMember;
